import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { trackFormSubmission } from "../utils/analytics";
import { getFormattedTimestamp } from "../utils/time";

const Popup = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
  });
  const [utmParams, setUtmParams] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Extract tracking parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const keys = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
      "gclid",
      "gbraid",
      "gad_source",
      "gad_campaignid",
    ];
    const extracted = {};
    keys.forEach((key) => {
      const v = params.get(key);
      if (v) extracted[key] = v;
    });
    setUtmParams(extracted);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");

    const scriptUrl =
      "https://script.google.com/macros/s/AKfycbywjCHWN3t0YK1_-YDmwj05w17xDHvjyYJxm-nwY4-yxtPAIJjYyw6RxyQsmAQ8jj3K/exec";

    try {
      // Use formatted timestamp
      const timestamp = getFormattedTimestamp();
      const payload = { ...formData, ...utmParams, timestamp };

      const body = new URLSearchParams();
      body.append("data", JSON.stringify(payload));

      await fetch(scriptUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
        mode: "no-cors",
      });

      setSuccessMessage(
        "Thank you for your submission! We will contact you within 24 hours."
      );

      // Send analytics via util
      trackFormSubmission("contact_form", {
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        page: window.location.pathname,
        timestamp,
        ...utmParams,
      });

      setFormData({ fullName: "", email: "", phone: "" });
    } catch (err) {
      console.error("Popup submit error:", err);
      setErrorMessage(
        "There was an error submitting your form. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center font-fira-sans">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Overlay Content */}
      <div
        className="relative z-[100] w-[90%] max-w-md p-4 md:p-6"
        style={{
          backgroundColor: "#141516",
          border: "1px solid rgba(255, 255, 255, 0.16)",
          borderRadius: "2px",
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 md:top-6 md:right-6 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-[2px] bg-white/10 hover:bg-white/20 transition-colors z-10"
          aria-label="Close"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 5L5 15M5 5L15 15"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <div className="mb-6">
          <h2 className="text-xl md:text-2xl text-white font-normal">Contact Us</h2>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="popup-fullName" className="block text-sm font-medium text-white/50">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="popup-fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Your Name"
              className="w-full mt-1 rounded-[2px] px-3 py-2 bg-white/10 border border-white/30 focus:outline-none focus:ring-2 focus:ring-[#B8EF43] text-white placeholder-white/30 font-fira-sans"
              required
            />
          </div>
          <div>
            <label htmlFor="popup-email" className="block text-sm font-medium text-white/50">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="popup-email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="example@gmail.com"
              className="w-full mt-1 px-3 py-2 rounded-[2px] bg-white/10 border border-white/30 focus:outline-none focus:ring-2 focus:ring-[#B8EF43] text-white placeholder-white/30 font-fira-sans"
              required
            />
          </div>
          <div>
            <label htmlFor="popup-phone" className="block text-sm font-medium text-white/50">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="popup-phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="XXXXXXXXXX"
              className="w-full mt-1 px-3 py-2 rounded-[2px] bg-white/10 border border-white/30 focus:outline-none focus:ring-2 focus:ring-[#B8EF43] text-white placeholder-white/30 font-fira-sans"
              required
            />
          </div>
          <button
            type="submit"
            className="w-full bg-[#B8EF43] text-black rounded-[2px] py-2 transition font-fira-sans hover:bg-[#B8EF43]/90"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </form>
        {successMessage && (
          <p className="mt-4 text-white/50 text-center">{successMessage}</p>
        )}
        {errorMessage && (
          <p className="mt-4 text-red-500 text-center">{errorMessage}</p>
        )}
      </div>
    </div>
  );
};

Popup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default Popup;
